import React, { useEffect, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";

import "../styles/institution.css";
import { AppShell } from "../components/AppShell";
import { StatusBadge } from "../components/StatusBadge";
import { EmptyState } from "../components/EmptyState";
import { PaginationBar, usePagination } from "../components/Pagination";
import { useAuth } from "../hooks/useAuth";
import { useIsMobile } from "../hooks/useIsMobile";
import { useT } from "../i18n/LanguageProvider";
import { fetchInstitutionDashboard } from "../api/institutions";
import {
  createBranch,
  deleteBranch,
  fetchMyBranches,
  fetchOrganisationDashboard,
  setBranchActive,
  updateBranch,
} from "../api/organisations";
import { BranchForm } from "../components/BranchForm";
import {
  listInstitutionRequests,
  listOrganisationRequests,
} from "../api/verifications";
import { createDownloadUrl } from "../api/storage";
import { fetchHRApplicantsApi, updateApplicantStatusApi } from "../api/jobs";
import {
  listInstitutionOfficers,
  listJoinRequests,
  listOrganisationInvites,
} from "../api/officers";
import { ExportRangeDialog } from "../components/ExportRangeDialog";
import { InlineIcon } from "../components/InlineIcon";
import { OrganisationProfileModal } from "../components/OrganisationProfileModal";
import { SafeLogo } from "../components/SafeLogo";
import { C, font } from "../styles/tokens";
import {
  attentionFigureNumber,
  attentionFigureShown,
  averageTurnaroundMs,
  formatTurnaround,
  openInvitationCount,
  openItemCount,
  percentOf,
  readAttentionCount,
  requestStatusCounts,
  waitingJoinRequestCount,
} from "../utils/dashboardCounts";
import {
  attachmentLabel,
  isAttachedFile,
  requestCandidateName,
  requestCredentialTitle,
} from "../utils/dashboardRequests";

// A "Needs attention" figure before it has been read.
const NOT_READ = { count: null, error: null };

/**
 * InstitutionDashboard - verifier home with the verification log table.
 *
 * Layout:
 *   1. Org header with name + status badge
 *   2. Three stat cards (pending, verified to date, active officers)
 *   3. Tabs (Pending | Verified | Rejected) with counts in tab labels
 *   4. Table with columns: Date | Candidate | Credential | Officer | Decision | Actions
 *   5. Sticky header on the table, hover state on rows, click to verify screen
 */
export function InstitutionDashboard() {
  const { localUser } = useAuth();
  const t = useT();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isInstitution = localUser?.type === "INSTITUTION_OFFICER";
  const isOrganisation = localUser?.type === "ORGANISATION_OFFICER";
  const myOfficerRecord = isInstitution
    ? localUser?.institutionOfficer
    : localUser?.organisationOfficer;
  const isAdmin =
    myOfficerRecord?.role === "PRIMARY_ADMIN" ||
    myOfficerRecord?.role === "ADMIN";
  const isOrgAdmin = isOrganisation && isAdmin;

  const [dashboard, setDashboard] = useState(null);
  const [allRequests, setAllRequests] = useState({
    pending: [],
    verified: [],
    rejected: [],
    withdrawn: [],
  });
  const [activeTab, setActiveTab] = useState("PENDING");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [cardModal, setCardModal] = useState(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  // "Needs attention" figures for admins, each { count, error }: officer
  // invitations not accepted yet, and (organisations) join requests to answer.
  const [invitations, setInvitations] = useState(NOT_READ);
  const [joinRequests, setJoinRequests] = useState(NOT_READ);

  // Soft-block: an officer who finished signup but never submitted the details form
  const needsInstitutionDetails =
    isInstitution && localUser && !localUser.institutionOfficer;
  const needsOrganisationDetails =
    isOrganisation && localUser && !localUser.organisationOfficer;
  const skipFetch = needsInstitutionDetails || needsOrganisationDetails;

  // An admin's "Needs attention" figures. Neither rejects: a failure is
  // kept and shown on its own card (readAttentionCount).
  const loadInvitationCount = async () =>
    setInvitations(
      await readAttentionCount(
        async () =>
          isInstitution
            ? openInvitationCount("institution", {
                officers: await listInstitutionOfficers(),
              })
            : openInvitationCount("organisation", {
                invites: await listOrganisationInvites(
                  myOfficerRecord?.organisationId,
                ),
              }),
        "Could not load the officer invitations.",
      ),
    );

  const loadJoinRequestCount = async () =>
    setJoinRequests(
      await readAttentionCount(
        async () =>
          waitingJoinRequestCount(
            await listJoinRequests(myOfficerRecord?.organisationId),
          ),
        "Could not load the join requests.",
      ),
    );

  const loadData = async () => {
    try {
      setLoading(true);
      const dashboardFn = isInstitution
        ? fetchInstitutionDashboard
        : fetchOrganisationDashboard;
      const listFn = isInstitution
        ? listInstitutionRequests
        : listOrganisationRequests;

      const [d, p, v, r, w] = await Promise.all([
        dashboardFn(),
        listFn("PENDING"),
        listFn("VERIFIED"),
        listFn("REJECTED"),
        listFn("WITHDRAWN"),
        ...(isAdmin ? [loadInvitationCount()] : []),
        ...(isOrgAdmin ? [loadJoinRequestCount()] : []),
      ]);
      setDashboard(d);
      setAllRequests({
        pending: p ?? [],
        verified: v ?? [],
        rejected: r ?? [],
        withdrawn: w ?? [],
      });
      setLastUpdatedAt(new Date().toISOString());
    } catch (err) {
      setError(err.message ?? t("common.somethingWrong"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (skipFetch) {
      setLoading(false);
      return;
    }
    if (!isInstitution && !isOrganisation) {
      setLoading(false);
      return;
    }
    loadData();
  }, [isInstitution, isOrganisation, skipFetch]);

  useEffect(() => {
    if (
      searchParams.get("onboarding") === "true" ||
      searchParams.get("setupProfile") === "true"
    ) {
      setProfileModalOpen(true);
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("onboarding");
      newParams.delete("setupProfile");
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  if (needsInstitutionDetails) {
    return <Navigate to="/register/institution/details" replace />;
  }
  if (needsOrganisationDetails) {
    return <Navigate to="/register/organisation/details" replace />;
  }
  const officerOrgStatus =
    localUser?.institutionOfficer?.institution?.status ??
    localUser?.organisationOfficer?.organisation?.status;
  if (
    officerOrgStatus === "PENDING_REVIEW" ||
    officerOrgStatus === "REJECTED"
  ) {
    return <Navigate to="/awaiting-approval" replace />;
  }

  const isBranchInactive =
    isOrganisation &&
    localUser?.organisationOfficer?.branch &&
    localUser?.organisationOfficer?.branch?.isActive === false;

  if (isBranchInactive) {
    return (
      <AppShell>
        <div
          style={{
            ...cardStyle,
            maxWidth: 600,
            margin: "40px auto",
            textAlign: "center",
            padding: 36,
          }}
        >
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: "50%",
              background: "#fee2e2",
              color: "#dc2626",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: 16,
            }}
          >
            <svg
              width="26"
              height="26"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
          </div>
          <h2
            style={{
              fontSize: 20,
              fontWeight: 700,
              color: "#0f172a",
              marginBottom: 8,
            }}
          >
            Branch Portal Deactivated
          </h2>
          <p
            style={{
              fontSize: 14,
              color: "#64748b",
              lineHeight: 1.6,
              marginBottom: 20,
            }}
          >
            This branch portal (
            <strong>
              {localUser?.organisationOfficer?.branch?.name || "Branch"}
            </strong>
            ) has been deactivated by your organisation administrator. Access to
            this portal is disabled while inactive.
          </p>
          <div style={{ fontSize: 13, color: "#94a3b8" }}>
            Please contact your Primary Administrator to reactivate this branch.
          </div>
        </div>
      </AppShell>
    );
  }

  if (!isInstitution && !isOrganisation) {
    return (
      <AppShell>
        <div style={cardStyle}>
          This page is for institution and organisation officers.
        </div>
      </AppShell>
    );
  }

  if (loading && !dashboard) {
    return (
      <AppShell>
        <div style={{ color: C.textMed, padding: 32, textAlign: "center" }}>
          {t("common.loading")}
        </div>
      </AppShell>
    );
  }

  const org = dashboard?.institution ?? dashboard?.organisation;

  // Formatted date (e.g. Tuesday, 25 August)
  const dateFormatted = new Date().toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  // Dynamic greeting based on hour
  const hour = new Date().getHours();
  const timeGreeting =
    hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

  // Every figure is counted from the request lists the server returned. A
  // request is pending, verified, rejected or withdrawn (its fee refunded);
  // the server has no "under review" state.
  const requestCounts = requestStatusCounts(allRequests);
  const totalApplications = requestCounts.total;
  const displayApproved = requestCounts.verified;
  const displayPending = requestCounts.pending;
  const displayRejected = requestCounts.rejected;
  const displayWithdrawn = requestCounts.withdrawn;

  // Percentages for the breakdown, donut and KPI cards
  const approvedPct = percentOf(displayApproved, totalApplications);
  const withdrawnPct = percentOf(displayWithdrawn, totalApplications);
  const pendingPct = percentOf(displayPending, totalApplications);
  const rejectedPct = percentOf(displayRejected, totalApplications);

  // "Needs attention": pending requests, and for an admin the officer
  // invitations not accepted yet and (organisations) the join requests to
  // answer, as the mobile app counts them.
  const showInvitations = isAdmin && attentionFigureShown(invitations);
  const showJoinRequests = isOrgAdmin && attentionFigureShown(joinRequests);
  const openItems = openItemCount({
    pendingRequests: displayPending,
    openInvitations: invitations.count,
    joinRequestsWaiting: joinRequests.count,
  });

  const tabs = [
    {
      key: "PENDING",
      label: t("dashboard.tabPending"),
      data: allRequests.pending,
    },
    {
      key: "VERIFIED",
      label: t("dashboard.tabVerified"),
      data: allRequests.verified,
    },
    {
      key: "REJECTED",
      label: t("dashboard.tabRejected"),
      data: allRequests.rejected,
    },
  ];
  const activeData = tabs.find((tab) => tab.key === activeTab)?.data ?? [];
  const RECENT_LIMIT = 3;
  const recentData = isInstitution
    ? activeData.slice(0, RECENT_LIMIT)
    : activeData;
  const allDecided = [...allRequests.verified, ...allRequests.rejected];
  const averageTurnaround = formatTurnaround(averageTurnaroundMs(allDecided));
  const realActivities = buildRealActivities(allRequests);

  return (
    <AppShell maxWidth="100%">
      {error && (
        <div style={{ ...cardStyle, color: C.red, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {/* Org status alert if rejected */}
      {org && org.status === "REJECTED" && org.rejectionReason && (
        <div
          style={{
            marginBottom: 20,
            padding: "12px 16px",
            background: C.redLight,
            borderRadius: 8,
            border: `1px solid ${C.red}33`,
            fontSize: 13,
            color: C.red,
          }}
        >
          <strong>Institution Approval Status: Rejected.</strong> Reason:{" "}
          {org.rejectionReason}
        </div>
      )}

      {/* Top Header Section matching design screenshot */}
      <div style={{ marginBottom: 24 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 6,
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 600, color: "#64748b" }}>
            {dateFormatted}
          </span>
          <button
            onClick={loadData}
            title="Refresh dashboard data"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontSize: 12,
              fontWeight: 600,
              color: "#475569",
              background: "transparent",
              border: 0,
              cursor: "pointer",
            }}
          >
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: "#10b981",
                display: "inline-block",
              }}
            />
            {lastUpdatedAt ? `Updated ${formatTime(lastUpdatedAt)}` : "Refresh"}
          </button>
        </div>

        <h1
          style={{
            fontSize: 32,
            fontWeight: 800,
            color: "#0f172a",
            margin: "0 0 6px",
            letterSpacing: "-0.5px",
          }}
        >
          Dashboard
        </h1>

        <div
          style={{
            fontSize: 18,
            fontWeight: 700,
            color: "#1e293b",
            marginBottom: 4,
          }}
        >
          {timeGreeting}
          {org?.name && (
            <>
              , <span style={{ color: "#0f172a" }}>{org.name}</span>
            </>
          )}
        </div>

        <div style={{ fontSize: 13, color: "#64748b", margin: 0 }}>
          Here's an overview of your{" "}
          {isInstitution ? "institution's" : "organisation's"} applications,
          officers, payments, and recent activity.
        </div>

        {/* Organization / Institution Profile Branding Banner */}
        <div
          style={{
            marginTop: 18,
            padding: "16px 20px",
            background: "#ffffff",
            borderRadius: 16,
            border: "1px solid #e2e8f0",
            boxShadow:
              "0 1px 3px rgba(15, 23, 42, 0.04), 0 4px 12px rgba(15, 23, 42, 0.02)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 16,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 16,
              minWidth: 0,
              flex: 1,
            }}
          >
            <div
              style={{
                width: 60,
                height: 60,
                borderRadius: 14,
                background: "#f8fafc",
                border: "2px solid #e2e8f0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                flexShrink: 0,
                boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
              }}
            >
              <SafeLogo
                src={org?.logoDownloadUrl}
                alt={org?.name}
                fallbackText={(org?.name || "TP")
                  .split(" ")
                  .map((w) => w[0])
                  .filter(Boolean)
                  .slice(0, 2)
                  .join("")
                  .toUpperCase()}
                imgStyle={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  padding: 4,
                }}
                fallbackStyle={{
                  fontSize: 20,
                  fontWeight: 800,
                  color: C.navy,
                  letterSpacing: -0.5,
                }}
              />
            </div>

            <div style={{ minWidth: 0, flex: 1 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  flexWrap: "wrap",
                  marginBottom: 3,
                }}
              >
                <span
                  style={{
                    fontSize: 17,
                    fontWeight: 700,
                    color: "#0f172a",
                    letterSpacing: "-0.2px",
                  }}
                >
                  {org?.name ||
                    (isInstitution ? "Academic Institution" : "Organisation")}
                </span>
                {org?.status && <StatusBadge status={org.status} />}
              </div>

              {org?.description ? (
                <p
                  style={{
                    fontSize: 13,
                    color: "#475569",
                    margin: "2px 0 0",
                    lineHeight: 1.4,
                  }}
                >
                  {org.description}
                </p>
              ) : (
                <p
                  style={{
                    fontSize: 12.5,
                    color: "#94a3b8",
                    fontStyle: "italic",
                    margin: "2px 0 0",
                  }}
                >
                  No one-line description added yet. Add a short summary to
                  represent your brand across the platform.
                </p>
              )}

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 14,
                  marginTop: 6,
                }}
              >
                {org?.websiteUrl && (
                  <a
                    href={
                      org.websiteUrl.startsWith("http")
                        ? org.websiteUrl
                        : `https://${org.websiteUrl}`
                    }
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      fontSize: 12,
                      color: "#2563eb",
                      textDecoration: "none",
                      fontWeight: 600,
                    }}
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <line x1="2" y1="12" x2="22" y2="12" />
                      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                    </svg>
                    {org.websiteUrl.replace(/^https?:\/\//, "")}
                  </a>
                )}

                {(org?.city || org?.state || org?.addressLine1) && (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      fontSize: 12,
                      color: "#64748b",
                      fontWeight: 500,
                    }}
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                    {[org.city, org.state].filter(Boolean).join(", ")}
                  </span>
                )}
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setProfileModalOpen(true)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "9px 16px",
              background: "#ffffff",
              color: "#1e293b",
              border: "1px solid #cbd5e1",
              borderRadius: 10,
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
              transition: "all 0.15s ease",
            }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
            Customize Profile
          </button>
        </div>
      </div>

      {/* 4 Metric KPI Cards Row with REAL DATA percentages & Hover/Click Functionality */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
          gap: 16,
          marginBottom: 24,
        }}
      >
        {/* Total Applications */}
        <KpiCard
          label="Total Applications"
          value={totalApplications.toLocaleString()}
          trend={totalApplications > 0 ? "100% of total" : "0% of total"}
          accentColor="#2563eb"
          onClick={() =>
            setCardModal({
              title: "Total Applications",
              requests: [
                ...(allRequests.pending || []),
                ...(allRequests.verified || []),
                ...(allRequests.rejected || []),
                ...(allRequests.withdrawn || []),
              ],
              statusKey: "ALL",
              accentColor: "#2563eb",
            })
          }
          icon={
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#2563eb"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="7" y1="17" x2="17" y2="7" />
              <polyline points="7 7 17 7 17 17" />
            </svg>
          }
        />

        {/* Pending Review */}
        <KpiCard
          label="Pending Review"
          value={displayPending.toLocaleString()}
          trend={`${pendingPct}% of total`}
          accentColor="#f59e0b"
          onClick={() =>
            setCardModal({
              title: "Pending Review Applications",
              requests: allRequests.pending || [],
              statusKey: "PENDING",
              accentColor: "#f59e0b",
            })
          }
          icon={
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#f59e0b"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
          }
        />

        {/* Approved */}
        <KpiCard
          label="Approved"
          value={displayApproved.toLocaleString()}
          trend={`${approvedPct}% of total`}
          accentColor="#10b981"
          onClick={() =>
            setCardModal({
              title: "Approved Applications",
              requests: allRequests.verified || [],
              statusKey: "VERIFIED",
              accentColor: "#10b981",
            })
          }
          icon={
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#10b981"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          }
        />

        {/* Rejected */}
        <KpiCard
          label="Rejected"
          value={displayRejected.toLocaleString()}
          trend={`${rejectedPct}% of total`}
          accentColor="#ef4444"
          onClick={() =>
            setCardModal({
              title: "Rejected Applications",
              requests: allRequests.rejected || [],
              statusKey: "REJECTED",
              accentColor: "#ef4444",
            })
          }
          icon={
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#ef4444"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          }
        />

        {/* Hiring Officers Quota for Academic Institutions and Organisations - Hidden/Commented per request
        {(isInstitution || isOrganisation) && (
          <KpiCard
            label="Hiring Officers"
            value={`${dashboard?.stats?.hiringOfficersCount ?? 0}/${dashboard?.stats?.hiringOfficersLimit ?? 2}`}
            trend={
              (dashboard?.stats?.hiringOfficersCount ?? 0) >=
              (dashboard?.stats?.hiringOfficersLimit ?? 2)
                ? "Quota limit reached"
                : `${(dashboard?.stats?.hiringOfficersLimit ?? 2) - (dashboard?.stats?.hiringOfficersCount ?? 0)} slots available`
            }
            accentColor="#6366f1"
            onClick={() => navigate("/officers")}
            icon={
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#6366f1"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            }
          />
        )}
        */}
      </div>

      {/* Middle Section: Application Overview & Quick Actions */}

      <div className="grid grid-cols-1 lg:grid-cols-[1.8fr_1fr] gap-5 mb-6">
        {/* Application Overview Card - Full Vertical Height */}
        <DashboardSectionCard
          accentColor="#2563eb"
          hoverBg="#f4f7fc"
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: 20,
              }}
            >
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: "#2563eb",
                    }}
                  />
                  <h2
                    style={{
                      fontSize: 18,
                      fontWeight: 700,
                      color: "#0f172a",
                      margin: 0,
                      letterSpacing: "-0.3px",
                    }}
                  >
                    Application Overview
                  </h2>
                </div>
                <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>
                  A real-time snapshot of every application status.
                </div>
              </div>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "4px 12px",
                  background: "#dcfce7",
                  color: "#15803d",
                  borderRadius: 16,
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: "#16a34a",
                  }}
                />
                {totalApplications.toLocaleString()} total
              </span>
            </div>

            <div className="flex flex-col sm:grid sm:grid-cols-[160px_1fr] gap-5 sm:gap-7 items-center">
              {/* Donut Chart */}
              <div style={{ position: "relative", width: 160, height: 160 }}>
                <DonutChart
                  approvedPct={approvedPct}
                  withdrawnPct={withdrawnPct}
                  pendingPct={pendingPct}
                  rejectedPct={rejectedPct}
                />
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                  }}
                >
                  <span
                    style={{
                      fontSize: 24,
                      fontWeight: 800,
                      color: "#0f172a",
                      lineHeight: 1.1,
                    }}
                  >
                    {totalApplications.toLocaleString()}
                  </span>
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 500,
                      color: "#64748b",
                      marginTop: 2,
                    }}
                  >
                    applications
                  </span>
                </div>
              </div>

              {/* Breakdown List */}
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <BreakdownRow
                  label="Approved"
                  color="#10b981"
                  count={displayApproved}
                  pct={approvedPct}
                  onClick={() =>
                    setCardModal({
                      title: "Approved Applications",
                      requests: allRequests.verified || [],
                      statusKey: "VERIFIED",
                      accentColor: "#10b981",
                    })
                  }
                />
                <BreakdownRow
                  label="Withdrawn"
                  color={WITHDRAWN_COLOR}
                  count={displayWithdrawn}
                  pct={withdrawnPct}
                  onClick={() =>
                    setCardModal({
                      title: "Withdrawn Applications",
                      requests: allRequests.withdrawn || [],
                      statusKey: "WITHDRAWN",
                      accentColor: WITHDRAWN_COLOR,
                    })
                  }
                />
                <BreakdownRow
                  label="Pending"
                  color="#f59e0b"
                  count={displayPending}
                  pct={pendingPct}
                  onClick={() =>
                    setCardModal({
                      title: "Pending Review Applications",
                      requests: allRequests.pending || [],
                      statusKey: "PENDING",
                      accentColor: "#f59e0b",
                    })
                  }
                />
                <BreakdownRow
                  label="Rejected"
                  color="#ef4444"
                  count={displayRejected}
                  pct={rejectedPct}
                  onClick={() =>
                    setCardModal({
                      title: "Rejected Applications",
                      requests: allRequests.rejected || [],
                      statusKey: "REJECTED",
                      accentColor: "#ef4444",
                    })
                  }
                />
              </div>
            </div>
          </div>

          {/* Bottom Summary Analytics Banner filling the card space */}
          <div
            style={{
              marginTop: 20,
              paddingTop: 16,
              borderTop: "1px solid #f1f5f9",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 16,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
              <div>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#64748b",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Approval Rate
                </div>
                <div
                  style={{
                    fontSize: 15,
                    fontWeight: 800,
                    color: "#10b981",
                    marginTop: 2,
                  }}
                >
                  {totalApplications > 0
                    ? `${Math.round((displayApproved / totalApplications) * 100)}%`
                    : "0%"}
                </div>
              </div>
              <div style={{ width: 1, height: 26, background: "#e2e8f0" }} />
              <div>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#64748b",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Avg Turnaround
                </div>
                <div
                  style={{
                    fontSize: 15,
                    fontWeight: 800,
                    color: "#2563eb",
                    marginTop: 2,
                  }}
                >
                  {averageTurnaround}
                </div>
              </div>
            </div>

            <button
              onClick={() => navigate("/inbox")}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 14px",
                borderRadius: 8,
                background: "#f8fafc",
                border: "1px solid #cbd5e1",
                fontSize: 12,
                fontWeight: 600,
                color: "#334155",
                cursor: "pointer",
                transition: "all 0.15s ease",
              }}
            >
              View Reports
            </button>
          </div>
        </DashboardSectionCard>

        {/* Quick Actions Card */}
        <DashboardSectionCard
          accentColor="#2563eb"
          hoverBg="#f4f7fc"
          style={{ display: "flex", flexDirection: "column" }}
        >
          <h2
            style={{
              fontSize: 18,
              fontWeight: 700,
              color: "#0f172a",
              margin: "0 0 2px",
              letterSpacing: "-0.3px",
            }}
          >
            Quick Actions
          </h2>
          <div style={{ fontSize: 13, color: "#64748b", marginBottom: 20 }}>
            Common administration tasks
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 12,
              flex: 1,
            }}
          >
            <QuickActionButton
              title="New Notifications"
              sub="Requests waiting for a decision"
              accentColor="#f59e0b"
              onClick={() => navigate("/inbox?tab=PENDING")}
              icon={
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#f59e0b"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
              }
            />

            <QuickActionButton
              title="Manage Officers"
              sub={
                (isInstitution || isOrganisation) && dashboard?.stats
                  ? `Verifiers: ${dashboard.stats.verificationOfficersCount ?? 0}/${dashboard.stats.verificationOfficersLimit ?? 2}`
                  : "View and manage officers"
              }
              accentColor="#6366f1"
              onClick={() => navigate("/officers")}
              icon={
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#6366f1"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              }
            />

            <QuickActionButton
              title="View Payments"
              sub="Check earnings and payment history"
              accentColor="#16a34a"
              onClick={() => navigate("/earnings")}
              icon={
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#16a34a"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="2" y="5" width="20" height="14" rx="2" />
                  <line x1="2" y1="10" x2="22" y2="10" />
                </svg>
              }
            />

            <QuickActionButton
              title="Verification Inbox"
              sub="Review pending applications"
              accentColor="#2563eb"
              onClick={() => navigate("/inbox?tab=PENDING")}
              icon={
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#2563eb"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
                  <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
                </svg>
              }
            />
          </div>
        </DashboardSectionCard>
      </div>

      {/* Needs Attention Section matching second screenshot */}
      <DashboardSectionCard
        accentColor="#f59e0b"
        hoverBg="#fffbeb"
        style={{ marginBottom: 24 }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 20,
          }}
        >
          <div>
            <h2
              style={{
                fontSize: 18,
                fontWeight: 700,
                color: "#0f172a",
                margin: 0,
                letterSpacing: "-0.3px",
              }}
            >
              Needs attention
            </h2>
            <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>
              {openItems > 0
                ? "These items are waiting for you or your team."
                : "Nothing is waiting for you right now."}
            </div>
          </div>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "4px 12px",
              background: "#fef3c7",
              color: "#b45309",
              borderRadius: 16,
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: "#f59e0b",
              }}
            />
            {openItems} open {openItems === 1 ? "item" : "items"}
          </span>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
            gap: 16,
          }}
        >
          {/* <AttentionCard
            number={displayPending}
            title="Applications pending"
            sub="Need verification review"
            badgeBg="#fef3c7"
            badgeColor="#b45309"
            onClick={() => navigate("/inbox?tab=PENDING")}
          /> */}

          {/* No failed-payments card: a request exists only once its fee
              is paid, and the server gives verifiers no failed payments. */}

          {showInvitations && (
            <AttentionCard
              number={attentionFigureNumber(invitations)}
              title={
                invitations.count === 1
                  ? "Officer invitation"
                  : "Officer invitations"
              }
              sub={invitations.error ?? "Awaiting acceptance"}
              badgeBg="#eff6ff"
              badgeColor="#2563eb"
              onClick={() => navigate("/officers")}
            />
          )}

          {showJoinRequests && (
            <AttentionCard
              number={attentionFigureNumber(joinRequests)}
              title={
                joinRequests.count === 1 ? "Join request" : "Join requests"
              }
              sub={joinRequests.error ?? "Answer them under Officers"}
              badgeBg="#eff6ff"
              badgeColor="#2563eb"
              onClick={() => navigate("/officers")}
            />
          )}
        </div>
      </DashboardSectionCard>

      {/* Recent Activity Section matching REAL-TIME data with timestamp */}
      <DashboardSectionCard
        accentColor="#2563eb"
        hoverBg="#f4f7fc"
        style={{ marginBottom: 24 }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 20,
          }}
        >
          <div>
            <h2
              style={{
                fontSize: 18,
                fontWeight: 700,
                color: "#0f172a",
                margin: 0,
                letterSpacing: "-0.3px",
              }}
            >
              Recent Activity
            </h2>
            <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>
              A live real-time view of verification officer actions and
              application updates
            </div>
          </div>
          <button
            onClick={() => navigate("/inbox?tab=ALL")}
            style={{
              background: "transparent",
              border: 0,
              color: "#2563eb",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            View All
          </button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {realActivities.length === 0 ? (
            <div
              style={{
                padding: "24px 0",
                textAlign: "center",
                color: "#64748b",
                fontSize: 13,
              }}
            >
              No recent activity recorded yet. Officer decisions and new
              incoming verification requests will appear here live.
            </div>
          ) : (
            realActivities.slice(0, 6).map((act) => (
              <ActivityItem
                key={act.id}
                title={act.title}
                sub={act.sub}
                time={act.timeFormatted}
                dotColor={act.dotColor}
                highlight={act.highlight}
                onClick={() => {
                  const targetTab =
                    act.status === "REJECTED"
                      ? "REJECTED"
                      : act.status === "VERIFIED"
                        ? "VERIFIED"
                        : "PENDING";
                  navigate(`/inbox?tab=${targetTab}`);
                }}
              />
            ))
          )}
        </div>
      </DashboardSectionCard>

      {/* Branch/office locations: Primary Admin and Admin officers only */}
      {isOrgAdmin && (
        <BranchesCard organisationId={org?.id} organisationName={org?.name} />
      )}

      {/* Applicants section: organisations only */}
      {/* {!isInstitution && <ApplicantsSection orgId={org?.id} />} */}

      <ExportRangeDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        items={allRequests.verified}
        orgName={org?.name}
      />

      {/* Interactive Modal for Card Details */}
      <CardListModal
        modalData={cardModal}
        onClose={() => setCardModal(null)}
        navigate={navigate}
      />

      {profileModalOpen && (
        <OrganisationProfileModal
          isOpen={profileModalOpen}
          onClose={() => setProfileModalOpen(false)}
          initialData={org}
          isInstitution={isInstitution}
          onProfileUpdated={() => {
            loadData();
          }}
        />
      )}
    </AppShell>
  );
}

// ---- dashboard sub-components ----

function DashboardSectionCard({
  children,
  style = {},
  accentColor = "#2563eb",
  hoverBg = "#eff6ff",
}) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? hoverBg : "#ffffff",
        borderRadius: 16,
        border: `1.5px solid ${hovered ? accentColor : "#e2e8f0"}`,
        padding: 24,
        boxShadow: hovered
          ? `0 12px 28px -4px ${accentColor}20, 0 4px 12px -4px rgba(0,0,0,0.04)`
          : "0 1px 3px rgba(0,0,0,0.03)",
        transform: hovered ? "translateY(-2px)" : "translateY(0)",
        transition: "all 0.25s cubic-bezier(0.16, 1, 0.3, 1)",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function KpiCard({ label, value, trend, accentColor, icon, onClick }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? accentColor : "#ffffff",
        borderRadius: 14,
        borderTop: `1px solid ${hovered ? accentColor : "#e2e8f0"}`,
        borderRight: `1px solid ${hovered ? accentColor : "#e2e8f0"}`,
        borderBottom: `1px solid ${hovered ? accentColor : "#e2e8f0"}`,
        borderLeft: `5px solid ${accentColor}`,
        padding: "20px 20px 18px",
        boxShadow: hovered
          ? `0 14px 28px -4px ${accentColor}55, 0 8px 12px -6px ${accentColor}30`
          : "0 1px 3px rgba(0,0,0,0.03)",
        transform: hovered ? "translateY(-4px)" : "translateY(0)",
        transition: "all 0.25s cubic-bezier(0.16, 1, 0.3, 1)",
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        position: "relative",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: hovered ? "#ffffff" : "#64748b",
            transition: "color 0.2s",
          }}
        >
          {label}
        </span>
        <div
          style={{
            width: 34,
            height: 34,
            borderRadius: "50%",
            background: hovered
              ? "rgba(255, 255, 255, 0.25)"
              : `${accentColor}14`,
            color: "#ffffff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.2s",
          }}
        >
          {React.cloneElement(icon, {
            stroke: hovered ? "#ffffff" : accentColor,
          })}
        </div>
      </div>

      <div
        style={{
          fontSize: 32,
          fontWeight: 800,
          color: hovered ? "#ffffff" : "#0f172a",
          margin: "10px 0 6px",
          fontVariantNumeric: "tabular-nums",
          lineHeight: 1,
          transition: "color 0.2s",
        }}
      >
        {value}
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: hovered ? "#ffffff" : accentColor,
            transition: "color 0.2s",
          }}
        >
          {trend}
        </div>
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: hovered ? "#ffffff" : "#94a3b8",
            display: "inline-flex",
            alignItems: "center",
            gap: 2,
            transition: "color 0.2s",
          }}
        >
          {hovered ? "View details" : "View"}
        </span>
      </div>
    </div>
  );
}

function ModalItemRow({
  name,
  titleText,
  dateStr,
  status,
  initials,
  accentColor,
  onNavigate,
}) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "16px 20px",
        borderRadius: 14,
        borderTop: `1px solid ${hovered ? accentColor : "#e2e8f0"}`,
        borderRight: `1px solid ${hovered ? accentColor : "#e2e8f0"}`,
        borderBottom: `1px solid ${hovered ? accentColor : "#e2e8f0"}`,
        borderLeft: `4px solid ${accentColor}`,
        background: "#ffffff",
        gap: 16,
        boxShadow: hovered
          ? `0 8px 20px -4px ${accentColor}20`
          : "0 1px 3px rgba(0,0,0,0.02)",
        transform: hovered ? "translateY(-2px)" : "translateY(0)",
        transition: "all 0.2s cubic-bezier(0.16, 1, 0.3, 1)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          flex: 1,
          minWidth: 0,
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            background: `${accentColor}15`,
            color: accentColor,
            fontWeight: 700,
            fontSize: 14,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {initials}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 3,
            }}
          >
            <span style={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>
              {name}
            </span>
            <StatusBadge status={status} />
          </div>
          <div style={{ fontSize: 13, color: "#475569", fontWeight: 500 }}>
            {titleText}
          </div>
          <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 3 }}>
            Submitted {dateStr}
          </div>
        </div>
      </div>

      <button
        onClick={onNavigate}
        style={{
          padding: "8px 16px",
          fontSize: 12,
          fontWeight: 700,
          color: hovered ? "#ffffff" : accentColor,
          background: hovered ? accentColor : `${accentColor}12`,
          border: `1px solid ${accentColor}30`,
          borderRadius: 8,
          cursor: "pointer",
          whiteSpace: "nowrap",
          transition: "all 0.2s ease",
        }}
      >
        View in Inbox
      </button>
    </div>
  );
}

function CardListModal({ modalData, onClose, navigate }) {
  const [closeHover, setCloseHover] = useState(false);
  const [btnHover, setBtnHover] = useState(false);

  if (!modalData) return null;

  const {
    title,
    requests = [],
    statusKey,
    accentColor = "#2563eb",
  } = modalData;

  // Helper icon for empty state based on accentColor / title
  const getEmptyIcon = () => {
    if (
      accentColor.includes("ef4444") ||
      accentColor.includes("dc2626") ||
      title.includes("Rejected")
    ) {
      return (
        <svg
          width="26"
          height="26"
          viewBox="0 0 24 24"
          fill="none"
          stroke={accentColor}
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="15" y1="9" x2="9" y2="15" />
          <line x1="9" y1="9" x2="15" y2="15" />
        </svg>
      );
    }
    if (
      accentColor.includes("10b981") ||
      accentColor.includes("16a34a") ||
      title.includes("Approved")
    ) {
      return (
        <svg
          width="26"
          height="26"
          viewBox="0 0 24 24"
          fill="none"
          stroke={accentColor}
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
      );
    }
    if (
      accentColor.includes("f59e0b") ||
      accentColor.includes("d97706") ||
      title.includes("Pending")
    ) {
      return (
        <svg
          width="26"
          height="26"
          viewBox="0 0 24 24"
          fill="none"
          stroke={accentColor}
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>
      );
    }
    return (
      <svg
        width="26"
        height="26"
        viewBox="0 0 24 24"
        fill="none"
        stroke={accentColor}
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <polyline points="10 9 9 9 8 9" />
      </svg>
    );
  };

  return (
    <div className="fixed inset-0 z-[1000] bg-slate-900/55 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-5">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl max-w-[720px] w-full max-h-[90vh] sm:max-h-[85vh] flex flex-col shadow-2xl overflow-hidden">
        {/* Top Accent Color Bar */}
        <div style={{ height: 4, background: accentColor, width: "100%" }} />

        {/* Modal Header */}
        <div className="px-4 py-3.5 sm:px-7 sm:py-5 border-b border-slate-100 flex justify-between items-center bg-white">
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  background: accentColor,
                  boxShadow: `0 0 10px ${accentColor}80`,
                }}
              />
              <h2
                style={{
                  fontSize: 20,
                  fontWeight: 800,
                  color: "#0f172a",
                  margin: 0,
                  letterSpacing: "-0.4px",
                }}
              >
                {title}
              </h2>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  padding: "3px 12px",
                  borderRadius: 20,
                  background: `${accentColor}12`,
                  color: accentColor,
                  border: `1px solid ${accentColor}25`,
                }}
              >
                {requests.length} {requests.length === 1 ? "record" : "records"}
              </span>
            </div>
            <div
              style={{
                fontSize: 13,
                color: "#64748b",
                marginTop: 5,
                fontWeight: 500,
              }}
            >
              Live list of verification records matching this card category.
            </div>
          </div>

          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            onMouseEnter={() => setCloseHover(true)}
            onMouseLeave={() => setCloseHover(false)}
            style={{
              background: closeHover ? "#f1f5f9" : "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: 10,
              width: 36,
              height: 36,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              color: closeHover ? "#0f172a" : "#64748b",
              transition: "all 0.15s ease",
            }}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Modal Body */}
        <div
          style={{
            padding: "28px",
            overflowY: "auto",
            flex: 1,
            background: "#f8fafc",
          }}
        >
          {requests.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "48px 24px",
                background: "#ffffff",
                borderRadius: 16,
                border: "1px solid #e2e8f0",
                boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
              }}
            >
              <div
                style={{
                  width: 60,
                  height: 60,
                  borderRadius: "50%",
                  background: `${accentColor}12`,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: 16,
                  border: `1.5px solid ${accentColor}25`,
                }}
              >
                {getEmptyIcon()}
              </div>
              <h3
                style={{
                  fontSize: 17,
                  fontWeight: 700,
                  color: "#0f172a",
                  margin: "0 0 6px",
                  letterSpacing: "-0.2px",
                }}
              >
                No records found
              </h3>
              <p
                style={{
                  fontSize: 13,
                  color: "#64748b",
                  margin: 0,
                  maxWidth: 380,
                  lineHeight: 1.5,
                  marginLeft: "auto",
                  marginRight: "auto",
                }}
              >
                There are currently no verification requests under this
                category.
              </p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {requests.map((item, idx) => {
                const name = requestCandidateName(item);
                const titleText = requestCredentialTitle(item);
                const dateStr = item.createdAt
                  ? new Date(item.createdAt).toLocaleDateString("en-IN", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })
                  : "Recently";
                const status = item.status ?? statusKey ?? "PENDING";
                const initials = name
                  .split(" ")
                  .map((n) => n[0])
                  .join("")
                  .substring(0, 2)
                  .toUpperCase();

                return (
                  <ModalItemRow
                    key={item.id || idx}
                    name={name}
                    titleText={titleText}
                    dateStr={dateStr}
                    status={status}
                    initials={initials}
                    accentColor={accentColor}
                    onNavigate={() => {
                      onClose();
                      const targetTab =
                        status === "VERIFIED" || status === "APPROVED"
                          ? "VERIFIED"
                          : status === "REJECTED"
                            ? "REJECTED"
                            : status === "PENDING"
                              ? "PENDING"
                              : statusKey || "ALL";
                      navigate(`/inbox?tab=${targetTab}`);
                    }}
                  />
                );
              })}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div
          style={{
            padding: "16px 28px",
            borderTop: "1px solid #f1f5f9",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: "#ffffff",
          }}
        >
          <button
            onClick={() => {
              onClose();
              const targetTab =
                statusKey === "VERIFIED" || statusKey === "APPROVED"
                  ? "VERIFIED"
                  : statusKey === "REJECTED"
                    ? "REJECTED"
                    : statusKey === "PENDING"
                      ? "PENDING"
                      : "ALL";
              navigate(`/inbox?tab=${targetTab}`);
            }}
            onMouseEnter={() => setBtnHover(true)}
            onMouseLeave={() => setBtnHover(false)}
            style={{
              padding: "10px 20px",
              fontSize: 13,
              fontWeight: 700,
              color: "#ffffff",
              background: accentColor,
              border: 0,
              borderRadius: 10,
              cursor: "pointer",
              boxShadow: btnHover
                ? `0 8px 20px -4px ${accentColor}60`
                : `0 4px 12px -2px ${accentColor}35`,
              transform: btnHover ? "translateY(-1px)" : "translateY(0)",
              transition: "all 0.2s ease",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            Open Full Inbox View
          </button>

          <button
            onClick={onClose}
            style={{
              padding: "10px 20px",
              fontSize: 13,
              fontWeight: 600,
              color: "#475569",
              background: "#ffffff",
              border: "1px solid #cbd5e1",
              borderRadius: 10,
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// A withdrawn request (fee refunded, closed undecided) reads as neutral.
const WITHDRAWN_COLOR = "#94a3b8";

function DonutChart({ approvedPct, withdrawnPct, pendingPct, rejectedPct }) {
  const r = 54;
  const c = 2 * Math.PI * r;

  const appLen = (approvedPct / 100) * c;
  const wdrLen = (withdrawnPct / 100) * c;
  const penLen = (pendingPct / 100) * c;
  const rejLen = (rejectedPct / 100) * c;

  const off1 = 0;
  const off2 = -appLen;
  const off3 = -(appLen + wdrLen);
  const off4 = -(appLen + wdrLen + penLen);

  return (
    <svg
      width="160"
      height="160"
      viewBox="0 0 160 160"
      style={{ transform: "rotate(-90deg)" }}
    >
      <circle
        cx="80"
        cy="80"
        r={r}
        fill="none"
        stroke="#f1f5f9"
        strokeWidth="16"
      />

      {approvedPct > 0 && (
        <circle
          cx="80"
          cy="80"
          r={r}
          fill="none"
          stroke="#10b981"
          strokeWidth="16"
          strokeDasharray={`${appLen} ${c - appLen}`}
          strokeDashoffset={off1}
        />
      )}
      {withdrawnPct > 0 && (
        <circle
          cx="80"
          cy="80"
          r={r}
          fill="none"
          stroke={WITHDRAWN_COLOR}
          strokeWidth="16"
          strokeDasharray={`${wdrLen} ${c - wdrLen}`}
          strokeDashoffset={off2}
        />
      )}
      {pendingPct > 0 && (
        <circle
          cx="80"
          cy="80"
          r={r}
          fill="none"
          stroke="#f59e0b"
          strokeWidth="16"
          strokeDasharray={`${penLen} ${c - penLen}`}
          strokeDashoffset={off3}
        />
      )}
      {rejectedPct > 0 && (
        <circle
          cx="80"
          cy="80"
          r={r}
          fill="none"
          stroke="#ef4444"
          strokeWidth="16"
          strokeDasharray={`${rejLen} ${c - rejLen}`}
          strokeDashoffset={off4}
        />
      )}
    </svg>
  );
}

function BreakdownRow({ label, color, count, pct, onClick }) {
  const [hovered, setHovered] = useState(false);

  const getHoverBg = (c) => {
    if (!c) return "#f8fafc";
    const lc = c.toLowerCase();
    if (lc.includes("2563eb") || lc.includes("blue")) return "#eff6ff"; // Blue tint
    if (lc.includes("f59e0b") || lc.includes("d97706") || lc.includes("amber"))
      return "#fffbeb"; // Amber tint
    if (lc.includes("10b981") || lc.includes("16a34a") || lc.includes("green"))
      return "#f0fdf4"; // Green tint
    if (lc.includes("ef4444") || lc.includes("dc2626") || lc.includes("red"))
      return "#fef2f2"; // Red tint
    return `${c}14`;
  };

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        padding: "8px 12px",
        borderRadius: 10,
        background: hovered ? getHoverBg(color) : "transparent",
        border: `1px solid ${hovered ? color : "transparent"}`,
        cursor: "pointer",
        transition: "all 0.2s cubic-bezier(0.16, 1, 0.3, 1)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontSize: 13,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            style={{
              width: 9,
              height: 9,
              borderRadius: "50%",
              background: color,
              boxShadow: hovered ? `0 0 8px ${color}` : "none",
              transition: "all 0.2s ease",
            }}
          />
          <span
            style={{
              fontWeight: hovered ? 700 : 600,
              color: hovered ? "#0f172a" : "#334155",
              transition: "color 0.2s ease",
            }}
          >
            {label}
          </span>
        </div>
        <div
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: hovered ? color : "#0f172a",
            transition: "color 0.2s ease",
          }}
        >
          {count.toLocaleString()}{" "}
          <span style={{ color: hovered ? color : "#94a3b8", fontWeight: 400 }}>
            ({pct}%)
          </span>
        </div>
      </div>

      <div
        style={{
          width: "100%",
          height: 6,
          background: hovered ? "#e2e8f0" : "#f1f5f9",
          borderRadius: 3,
          overflow: "hidden",
          transition: "background 0.2s ease",
        }}
      >
        <div
          style={{
            width: `${Math.min(100, Math.max(0, pct))}%`,
            height: "100%",
            background: color,
            borderRadius: 3,
            transition: "width 0.5s ease",
          }}
        />
      </div>
    </div>
  );
}

function QuickActionButton({
  title,
  sub,
  icon,
  accentColor = "#2563eb",
  hoverBg,
  onClick,
}) {
  const [hover, setHover] = useState(false);

  const getHoverBg = (c) => {
    if (!c) return "#eff6ff";
    const lc = c.toLowerCase();
    if (
      lc.includes("f59e0b") ||
      lc.includes("d97706") ||
      lc.includes("amber") ||
      lc.includes("orange")
    )
      return "#fffbeb";
    if (lc.includes("10b981") || lc.includes("16a34a") || lc.includes("green"))
      return "#f0fdf4";
    if (
      lc.includes("8b5cf6") ||
      lc.includes("6366f1") ||
      lc.includes("purple") ||
      lc.includes("indigo")
    )
      return "#f5f3ff";
    if (lc.includes("dc2626") || lc.includes("ef4444") || lc.includes("red"))
      return "#fef2f2";
    return "#eff6ff"; // Default blue tint
  };

  const bg = hoverBg || getHoverBg(accentColor);

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "14px 16px",
        borderRadius: 12,
        borderTop: `1px solid ${hover ? accentColor : "#e2e8f0"}`,
        borderRight: `1px solid ${hover ? accentColor : "#e2e8f0"}`,
        borderBottom: `1px solid ${hover ? accentColor : "#e2e8f0"}`,
        borderLeft: `4px solid ${accentColor}`,
        background: hover ? bg : "#ffffff",
        boxShadow: hover
          ? `0 8px 20px -4px ${accentColor}25`
          : "0 1px 2px rgba(0,0,0,0.02)",
        transform: hover ? "translateX(4px)" : "translateX(0)",
        cursor: "pointer",
        transition: "all 0.25s cubic-bezier(0.16, 1, 0.3, 1)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <div
          style={{
            width: 38,
            height: 38,
            borderRadius: "50%",
            background: hover ? accentColor : `${accentColor}14`,
            color: hover ? "#ffffff" : accentColor,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            transition: "all 0.2s ease",
          }}
        >
          {React.cloneElement(icon, {
            stroke: hover ? "#ffffff" : accentColor,
          })}
        </div>
        <div>
          <div
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: hover ? "#0f172a" : "#1e293b",
              transition: "color 0.2s",
            }}
          >
            {title}
          </div>
          <div
            style={{
              fontSize: 12,
              color: hover ? accentColor : "#64748b",
              transition: "color 0.2s",
              fontWeight: hover ? 600 : 400,
            }}
          >
            {sub}
          </div>
        </div>
      </div>

      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke={hover ? accentColor : "#94a3b8"}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{
          transition: "all 0.2s",
          transform: hover ? "translateX(2px)" : "none",
        }}
      >
        <polyline points="9 18 15 12 9 6" />
      </svg>
    </div>
  );
}

function AttentionCard({
  number,
  title,
  sub,
  badgeBg,
  badgeColor,
  accentColor = "#2563eb",
  onClick,
}) {
  const [hover, setHover] = useState(false);

  const getHoverBg = (c) => {
    if (!c) return "#eff6ff";
    const lc = c.toLowerCase();
    if (
      lc.includes("f59e0b") ||
      lc.includes("d97706") ||
      lc.includes("amber") ||
      lc.includes("b45309")
    )
      return "#fffbeb";
    if (
      lc.includes("10b981") ||
      lc.includes("16a34a") ||
      lc.includes("green") ||
      lc.includes("15803d")
    )
      return "#f0fdf4";
    if (
      lc.includes("dc2626") ||
      lc.includes("ef4444") ||
      lc.includes("red") ||
      lc.includes("b91c1c")
    )
      return "#fef2f2";
    return "#eff6ff";
  };

  const activeAccent = badgeColor || accentColor;
  const hoverBg = getHoverBg(activeAccent);

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "16px 18px",
        borderRadius: 12,
        borderTop: `1px solid ${hover ? activeAccent : "#e2e8f0"}`,
        borderRight: `1px solid ${hover ? activeAccent : "#e2e8f0"}`,
        borderBottom: `1px solid ${hover ? activeAccent : "#e2e8f0"}`,
        borderLeft: `4px solid ${activeAccent}`,
        background: hover ? hoverBg : "#ffffff",
        boxShadow: hover
          ? `0 8px 20px -4px ${activeAccent}25`
          : "0 1px 2px rgba(0,0,0,0.02)",
        transform: hover ? "translateY(-3px)" : "translateY(0)",
        cursor: "pointer",
        transition: "all 0.25s cubic-bezier(0.16, 1, 0.3, 1)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <div
          style={{
            width: 38,
            height: 38,
            borderRadius: 10,
            background: hover ? activeAccent : badgeBg,
            color: hover ? "#ffffff" : badgeColor,
            fontSize: 15,
            fontWeight: 800,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            transition: "all 0.2s ease",
          }}
        >
          {number}
        </div>
        <div>
          <div
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: hover ? "#0f172a" : "#1e293b",
            }}
          >
            {title}
          </div>
          <div
            style={{
              fontSize: 12,
              color: hover ? activeAccent : "#64748b",
              fontWeight: hover ? 600 : 400,
            }}
          >
            {sub}
          </div>
        </div>
      </div>

      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#94a3b8"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polyline points="9 18 15 12 9 6" />
      </svg>
    </div>
  );
}

function formatRelativeTime(dateInput) {
  if (!dateInput) return "Recently";
  const date = new Date(dateInput);
  if (isNaN(date.getTime())) return "Recently";
  const now = new Date();
  const diffInSeconds = Math.max(
    0,
    Math.floor((now.getTime() - date.getTime()) / 1000),
  );

  if (diffInSeconds < 60) return "Just now";
  if (diffInSeconds < 3600) {
    const mins = Math.floor(diffInSeconds / 60);
    return `${mins} ${mins === 1 ? "minute" : "minutes"} ago`;
  }
  if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
  }
  if (diffInSeconds < 172800) return "Yesterday";
  return date.toLocaleDateString("en-IN", { month: "short", day: "numeric" });
}

function buildRealActivities(allRequests) {
  const activities = [];

  // 1. Pending / New requests
  (allRequests.pending || []).forEach((req) => {
    const name = requestCandidateName(req);
    const title = requestCredentialTitle(req);
    const time = req.createdAt || req.updatedAt || new Date().toISOString();
    activities.push({
      id: `pending-${req.id}`,
      title: "New application received",
      sub: `${name} | ${title}`,
      timeRaw: new Date(time).getTime(),
      timeFormatted: formatRelativeTime(time),
      dotColor: "#2563eb",
      highlight: true,
      status: "PENDING",
      req,
    });
  });

  // 2. Approved requests
  (allRequests.verified || []).forEach((req) => {
    const name = requestCandidateName(req);
    const title = requestCredentialTitle(req);
    const officer =
      req.decidedBy?.institutionOfficer?.fullName ||
      req.decidedBy?.organisationOfficer?.fullName ||
      "Officer";
    const time =
      req.decidedAt ||
      req.updatedAt ||
      req.createdAt ||
      new Date().toISOString();
    activities.push({
      id: `verified-${req.id}`,
      title: "Application approved",
      sub: `${name} | ${title} (Approved by ${officer})`,
      timeRaw: new Date(time).getTime(),
      timeFormatted: formatRelativeTime(time),
      dotColor: "#10b981",
      highlight: false,
      status: "VERIFIED",
      req,
    });
  });

  // 3. Rejected requests
  (allRequests.rejected || []).forEach((req) => {
    const name = requestCandidateName(req);
    const title = requestCredentialTitle(req);
    const officer =
      req.decidedBy?.institutionOfficer?.fullName ||
      req.decidedBy?.organisationOfficer?.fullName ||
      "Officer";
    const time =
      req.decidedAt ||
      req.updatedAt ||
      req.createdAt ||
      new Date().toISOString();
    activities.push({
      id: `rejected-${req.id}`,
      title: "Application rejected",
      sub: `${name} | ${title} (Reviewed by ${officer})`,
      timeRaw: new Date(time).getTime(),
      timeFormatted: formatRelativeTime(time),
      dotColor: "#ef4444",
      highlight: false,
      status: "REJECTED",
      req,
    });
  });

  // Sort newest first
  activities.sort((a, b) => b.timeRaw - a.timeRaw);
  return activities;
}

function ActivityItem({ title, sub, time, dotColor, highlight, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "14px 18px",
        borderRadius: 12,
        background: hover ? "#f8fafc" : highlight ? "#eff6ff" : "#ffffff",
        border: `1px solid ${hover ? "#cbd5e1" : highlight ? "#dbeafe" : "#f1f5f9"}`,
        cursor: onClick ? "pointer" : "default",
        transition: "all 0.15s ease",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <span
          style={{
            width: 9,
            height: 9,
            borderRadius: "50%",
            background: dotColor || "#2563eb",
            flexShrink: 0,
          }}
        />
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>
            {title}
          </div>
          <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
            {sub}
          </div>
        </div>
      </div>

      <div style={{ fontSize: 12, color: "#94a3b8", fontWeight: 500 }}>
        {time}
      </div>
    </div>
  );
}

// ---- branches (organisations only) ----

/**
 * Branch/office manager for the parent organisation.
 *
 * Every branch here belongs to ONE parent organisation and is created, edited
 * and deactivated through that organisation's id in the URL. The server
 * authorises against that id, so an officer can only ever touch their own
 * organisation's branches.
 *
 * Deleting a branch removes its row: the branch login stops working, and
 * its officers, join requests and invitations lose the branch but stay.
 * Deactivating takes a branch out of every selection list while keeping the
 * record intact.
 */
function BranchesCard({ organisationId, organisationName }) {
  const isMobile = useIsMobile();
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);

  async function reload() {
    if (!organisationId) return;
    try {
      const items = await fetchMyBranches();
      // Defensive: never let an unexpected shape crash the whole dashboard.
      setBranches(Array.isArray(items) ? items : []);
      setError(null);
    } catch (err) {
      setError(err?.message ?? "Could not load branches.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organisationId]);

  const activeCount = branches.filter((b) => b.isActive !== false).length;

  async function handleCreate(values) {
    await createBranch(organisationId, values);
    setAdding(false);
    await reload();
  }

  async function handleUpdate(branchId, values) {
    await updateBranch(organisationId, branchId, values);
    setEditingId(null);
    await reload();
  }

  async function handleToggleActive(branch) {
    setError(null);
    try {
      await setBranchActive(
        organisationId,
        branch.id,
        branch.isActive === false,
      );
      await reload();
    } catch (err) {
      setError(err?.message ?? "Could not change the branch status.");
    }
  }

  const [deletingBranch, setDeletingBranch] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  async function handleConfirmDelete() {
    if (!deletingBranch) return;
    setIsDeleting(true);
    setError(null);
    try {
      await deleteBranch(organisationId, deletingBranch.id);
      setDeletingBranch(null);
      await reload();
    } catch (err) {
      setError(err?.message ?? "Could not delete the branch.");
    } finally {
      setIsDeleting(false);
    }
  }

  return (
    <section style={{ ...cardStyle, marginBottom: 16 }}>
      <DeleteBranchModal
        open={Boolean(deletingBranch)}
        branch={deletingBranch}
        deleting={isDeleting}
        onClose={() => !isDeleting && setDeletingBranch(null)}
        onConfirm={handleConfirmDelete}
      />
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          cursor: "pointer",
        }}
        onClick={() => setOpen((o) => !o)}
      >
        <div>
          <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>
            Branch Management
            {activeCount > 0 && (
              <span
                style={{
                  marginLeft: 8,
                  padding: "2px 10px",
                  fontSize: 11,
                  fontWeight: 700,
                  color: C.green,
                  background: C.greenLight,
                  borderRadius: 12,
                }}
              >
                {activeCount} active
              </span>
            )}
          </h2>
          <div style={{ fontSize: 12, color: C.textMed, marginTop: 2 }}>
            Office locations of{" "}
            <strong>{organisationName || "your organisation"}</strong>. All
            branches share the organisation GSTIN: a branch is a location, not a
            separate registration.
          </div>
        </div>
        <span style={{ fontSize: 18, color: C.textLight }}>
          <InlineIcon
            name="chevronRight"
            size={16}
            className={open ? "-rotate-90" : "rotate-90"}
          />
        </span>
      </div>

      {open && (
        <div style={{ marginTop: 14 }}>
          {error && (
            <div
              role="alert"
              style={{ fontSize: 12, color: C.red, marginBottom: 10 }}
            >
              {error}
            </div>
          )}

          {loading ? (
            <div style={{ fontSize: 13, color: C.textMed, padding: "4px 0" }}>
              Loading branches...
            </div>
          ) : branches.length === 0 ? (
            <div
              style={{ fontSize: 13, color: C.textMed, padding: "4px 0 10px" }}
            >
              No branches available. Please add a branch to continue.
            </div>
          ) : (
            <div style={{ marginBottom: 12 }}>
              {branches.map((b) =>
                editingId === b.id ? (
                  <div
                    key={b.id}
                    style={{
                      padding: "10px 0",
                      borderBottom: `1px solid ${C.divider}`,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: C.textDark,
                        marginBottom: 6,
                      }}
                    >
                      Editing {b.name}
                    </div>
                    <BranchForm
                      initial={{
                        name: b.name ?? "",
                        addressLine1: b.addressLine1 ?? "",
                        city: b.city ?? "",
                        state: b.state ?? "",
                        pincode: b.pincode ?? "",
                        branchCode: b.branchCode ?? "",
                        email: b.email ?? "",
                        password: "",
                        isHeadOffice: !!b.isHeadOffice,
                      }}
                      existingNames={branches
                        .filter((o) => o.id !== b.id)
                        .map((o) => o.name)}
                      existingCodes={branches
                        .filter((o) => o.id !== b.id && o.branchCode)
                        .map((o) => o.branchCode)}
                      submitLabel="Save changes"
                      onCancel={() => setEditingId(null)}
                      onSubmit={(values) => handleUpdate(b.id, values)}
                    />
                  </div>
                ) : (
                  <BranchRow
                    key={b.id}
                    branch={b}
                    onEdit={() => {
                      setAdding(false);
                      setEditingId(b.id);
                    }}
                    onToggleActive={() => handleToggleActive(b)}
                    onDelete={() => setDeletingBranch(b)}
                  />
                ),
              )}
            </div>
          )}

          {adding ? (
            <div
              style={{ borderTop: `1px solid ${C.divider}`, paddingTop: 12 }}
            >
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: C.textDark,
                  marginBottom: 6,
                }}
              >
                New branch
              </div>
              <BranchForm
                existingNames={branches.map((b) => b.name)}
                existingCodes={branches
                  .filter((b) => b.branchCode)
                  .map((b) => b.branchCode)}
                submitLabel="Add branch"
                onCancel={() => setAdding(false)}
                onSubmit={handleCreate}
              />
            </div>
          ) : (
            <button
              type="button"
              onClick={() => {
                setEditingId(null);
                setAdding(true);
              }}
              style={{
                width: isMobile ? "100%" : "auto",
                minHeight: isMobile ? 44 : "auto",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "9px 18px",
                fontSize: 13,
                fontWeight: 600,
                color: "white",
                background: C.blue,
                border: 0,
                borderRadius: 4,
                cursor: "pointer",
                fontFamily: font,
              }}
            >
              + Add Branch
            </button>
          )}
        </div>
      )}
    </section>
  );
}

/** One row in the branch list: details, status pill, Edit / Deactivate / Delete. */
function BranchRow({ branch, onEdit, onToggleActive, onDelete }) {
  const isMobile = useIsMobile();
  const isActive = branch.isActive !== false;
  const where = [branch.city, branch.state].filter(Boolean).join(", ");

  return (
    <div
      style={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        justifyContent: "space-between",
        alignItems: isMobile ? "stretch" : "flex-start",
        gap: isMobile ? 10 : 12,
        padding: isMobile ? "12px 0" : "10px 0",
        borderBottom: `1px solid ${C.divider}`,
        fontSize: 13,
        opacity: isActive ? 1 : 0.62,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <strong style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}>
            {branch.name}
          </strong>
          <span
            style={{
              padding: "1px 8px",
              fontSize: 10,
              fontWeight: 700,
              textTransform: "uppercase",
              color: isActive ? C.green : C.textMed,
              background: isActive ? C.greenLight : C.divider,
              borderRadius: 8,
              whiteSpace: "nowrap",
            }}
          >
            {isActive ? "Active" : "Inactive"}
          </span>
          {branch.isHeadOffice && (
            <span
              style={{
                padding: "1px 8px",
                fontSize: 10,
                fontWeight: 700,
                textTransform: "uppercase",
                color: C.blue,
                background: C.blueLight,
                borderRadius: 8,
                whiteSpace: "nowrap",
              }}
            >
              Head office
            </span>
          )}
        </div>
        {branch.addressLine1 && (
          <div
            style={{
              color: C.textMed,
              marginTop: 2,
              overflowWrap: "anywhere",
              wordBreak: "break-word",
            }}
          >
            {branch.addressLine1}
          </div>
        )}
        <div style={{ color: C.textMed, marginTop: 2 }}>
          {where}
          {branch.pincode ? ` - ${branch.pincode}` : ""}
        </div>
        {branch.email && (
          <div
            style={{
              color: C.textMed,
              marginTop: 2,
              fontSize: 12,
              overflowWrap: "anywhere",
              wordBreak: "break-word",
            }}
          >
            {branch.email}
          </div>
        )}
        {branch.branchCode && (
          <div style={{ color: C.textLight, marginTop: 2, fontSize: 12 }}>
            Branch Code: {branch.branchCode}
          </div>
        )}
      </div>

      <div
        style={{
          display: "flex",
          gap: isMobile ? 8 : 10,
          flexShrink: 0,
          flexWrap: "wrap",
          marginTop: isMobile ? 4 : 0,
          paddingTop: isMobile ? 6 : 0,
          borderTop: isMobile ? `1px dashed ${C.divider}` : "none",
        }}
      >
        <button
          type="button"
          onClick={onEdit}
          style={{
            ...linkButtonStyle(C.blue),
            minHeight: isMobile ? 38 : "auto",
            padding: isMobile ? "6px 12px" : 0,
            background: isMobile ? (C.blueLight ?? "#eff6ff") : "transparent",
            borderRadius: isMobile ? 6 : 0,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          Edit
        </button>
        <button
          type="button"
          onClick={onToggleActive}
          title={
            isActive
              ? "Deactivate - keeps history, hides it from new selections"
              : "Reactivate this branch"
          }
          style={{
            ...linkButtonStyle(isActive ? (C.orange ?? "#d97706") : C.green),
            minHeight: isMobile ? 38 : "auto",
            padding: isMobile ? "6px 12px" : 0,
            background: isMobile
              ? isActive
                ? (C.orangeLight ?? "#fef3c7")
                : (C.greenLight ?? "#dcfce7")
              : "transparent",
            borderRadius: isMobile ? 6 : 0,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isActive ? "Deactivate" : "Activate"}
        </button>
        <button
          type="button"
          onClick={onDelete}
          title="Delete this branch permanently"
          style={{
            ...linkButtonStyle(C.red),
            minHeight: isMobile ? 38 : "auto",
            padding: isMobile ? "6px 12px" : 0,
            background: isMobile ? (C.redLight ?? "#fee2e2") : "transparent",
            borderRadius: isMobile ? 6 : 0,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          Delete
        </button>
      </div>
    </div>
  );
}

/** Modal dialog to confirm permanent deletion of a branch. */
function DeleteBranchModal({ open, branch, deleting, onClose, onConfirm }) {
  const isMobile = useIsMobile();
  if (!open || !branch) return null;

  return (
    <div
      onClick={deleting ? undefined : onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 200,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: isMobile ? 12 : 16,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 440,
          background: C.bg,
          borderRadius: 12,
          boxShadow: "0 20px 50px rgba(0,0,0,0.25)",
          fontFamily: font,
          overflow: "hidden",
        }}
      >
        <div
          style={{ padding: isMobile ? "16px 16px 12px" : "20px 22px 14px" }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: 10,
            }}
          >
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: "50%",
                background: C.redLight ?? "#fee2e2",
                color: C.red ?? "#dc2626",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 18,
                flexShrink: 0,
              }}
            >
              <InlineIcon name="warning" size={18} />
            </div>
            <h2
              style={{
                fontSize: 17,
                fontWeight: 700,
                margin: 0,
                color: C.textDark,
              }}
            >
              Delete Branch
            </h2>
          </div>
          <p
            style={{
              fontSize: 13,
              color: C.textMed,
              margin: "6px 0 0",
              lineHeight: 1.5,
              overflowWrap: "anywhere",
              wordBreak: "break-word",
            }}
          >
            Are you sure you want to permanently delete the branch{" "}
            <strong style={{ color: C.textDark }}>"{branch.name}"</strong>
            {branch.city ? ` in ${branch.city}` : ""}?
          </p>
          <div
            style={{
              marginTop: 12,
              padding: "10px 12px",
              background: C.bgMuted ?? "#f8fafc",
              border: `1px solid ${C.divider ?? "#e2e8f0"}`,
              borderRadius: 8,
              fontSize: 12,
              color: C.textMed,
              lineHeight: 1.4,
            }}
          >
            This action cannot be undone. The branch login stops working.
            Officers, invitations and join requests linked to this branch stay,
            without a branch.
          </div>
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "flex-end",
            gap: 10,
            padding: isMobile ? "12px 16px" : "14px 22px",
            borderTop: `1px solid ${C.divider}`,
            background: C.bgMuted,
          }}
        >
          <button
            type="button"
            onClick={onConfirm}
            disabled={deleting}
            style={{
              order: isMobile ? 1 : 2,
              width: isMobile ? "100%" : "auto",
              minHeight: isMobile ? 44 : "auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "9px 18px",
              fontSize: 13,
              fontWeight: 600,
              color: "white",
              background: C.red ?? "#dc2626",
              border: 0,
              borderRadius: 6,
              cursor: deleting ? "not-allowed" : "pointer",
              fontFamily: font,
              opacity: deleting ? 0.7 : 1,
            }}
          >
            {deleting ? "Deleting..." : "Delete Branch"}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={deleting}
            style={{
              order: isMobile ? 2 : 1,
              width: isMobile ? "100%" : "auto",
              minHeight: isMobile ? 44 : "auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "9px 18px",
              fontSize: 13,
              fontWeight: 600,
              color: C.textDark,
              background: "white",
              border: `1px solid ${C.dividerStrong ?? "#cbd5e1"}`,
              borderRadius: 6,
              cursor: deleting ? "not-allowed" : "pointer",
              fontFamily: font,
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

const linkButtonStyle = (color) => ({
  background: "transparent",
  border: 0,
  padding: 0,
  color,
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: font,
});

// ---- table ----

// Read-only preview: same single-row data shape as the inbox, but rows are
// not clickable. Officers act on requests from the inbox ("See all").
function VerificationTable({ requests }) {
  const pager = usePagination(requests, 10);
  return (
    <>
      <div
        style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}
        className="w-full -mx-1 sm:mx-0 px-1 sm:px-0"
        data-scrolltable
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: 13,
            fontFamily: font,
          }}
        >
          <thead>
            <tr>
              <Th style={{ width: 104 }}>DATE</Th>
              <Th style={{ width: 180 }}>CANDIDATE</Th>
              <Th>CREDENTIAL DETAILS</Th>
              <Th style={{ width: 104 }}>CERTIFICATE</Th>
              <Th style={{ width: 120 }}>OFFICER</Th>
              <Th style={{ width: 130 }}>DECISION</Th>
            </tr>
          </thead>
          <tbody>
            {pager.pageItems.map((req) => (
              <LogRow key={req.id} req={req} />
            ))}
          </tbody>
        </table>
      </div>
      <PaginationBar pager={pager} itemLabel="requests" />
    </>
  );
}

function Th({ children, style: extraStyle }) {
  return (
    <th
      style={{
        padding: "10px 16px",
        textAlign: "left",
        fontSize: 11,
        fontWeight: 700,
        color: C.textLight,
        letterSpacing: 0.6,
        background: C.bgMuted,
        borderBottom: `1px solid ${C.divider}`,
        whiteSpace: "nowrap",
        ...extraStyle,
      }}
    >
      {children}
    </th>
  );
}

// Open the certificate in a new tab. The tab is opened synchronously (popup-safe)
// and then redirected to the presigned URL.
async function openCertificate(req) {
  const key = req.credential?.certificateS3Key;
  if (!key) return;
  const win = window.open("", "_blank", "noopener");
  try {
    const { downloadUrl } = await createDownloadUrl(key);
    if (win) win.location = downloadUrl;
    else window.open(downloadUrl, "_blank", "noopener");
  } catch {
    if (win) win.close();
  }
}

function LogRow({ req }) {
  const [hover, setHover] = useState(false);
  const cred = req.credential ?? {};

  const fullName =
    `${req.professional?.firstName ?? ""} ${req.professional?.lastName ?? ""}`.trim() ||
    "-";
  const email = req.professional?.user?.email;
  const officerName = getOfficerName(req.decidedBy);
  const isEdit = req.context === "EDIT";
  const metaPairs = metadataPairs(cred.metadata);
  const hasCert = !!cred.certificateS3Key;

  return (
    <tr
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: hover ? "#F6FAF7" : C.bg,
        borderTop: `1px solid ${C.divider}`,
        verticalAlign: "top",
      }}
    >
      <td style={cellStyle}>
        <span style={{ fontSize: 13, color: C.textMed }}>
          {formatDate(req.decidedAt ?? req.createdAt)}
        </span>
      </td>

      <td style={cellStyle}>
        <div style={{ fontWeight: 600, color: C.textDark }}>{fullName}</div>
        {email && (
          <div
            style={{ fontSize: 12, color: C.textMed, wordBreak: "break-all" }}
          >
            {email}
          </div>
        )}
      </td>

      <td style={{ ...cellStyle, maxWidth: 420 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <span style={{ fontWeight: 700, color: C.textDark }}>
            {cred.title || "-"}
          </span>
          {isEdit && (
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: C.amber,
                background: C.amberLight,
                padding: "1px 7px",
                borderRadius: 10,
                textTransform: "uppercase",
                letterSpacing: 0.4,
              }}
            >
              Edit request
            </span>
          )}
        </div>

        <div style={{ fontSize: 12, color: C.textMed, marginTop: 2 }}>
          {[cred.issuer, labelType(cred.type)].filter(Boolean).join(" | ")}
          {cred.startDate && (
            <>
              {" "}
              | {formatDateRange(cred.startDate, cred.endDate, cred.isCurrent)}
            </>
          )}
        </div>

        {metaPairs.length > 0 && (
          <div style={{ fontSize: 12, color: C.textDark, marginTop: 4 }}>
            {metaPairs.map(([k, v], i) => (
              <span key={k}>
                {i > 0 && <span style={{ color: C.textLight }}> | </span>}
                <span style={{ color: C.textLight }}>{humanizeKey(k)}: </span>
                <span style={{ fontWeight: 500 }}>{String(v)}</span>
              </span>
            ))}
          </div>
        )}

        {cred.description && (
          <div
            style={{
              fontSize: 12,
              color: C.textMed,
              marginTop: 4,
              lineHeight: 1.45,
              whiteSpace: "pre-line",
            }}
          >
            {cred.description}
          </div>
        )}

        {isEdit && req.proposedChanges && (
          <ProposedChangesInline changes={req.proposedChanges} current={cred} />
        )}

        {req.requestNote && (
          <div style={{ fontSize: 12, color: C.textDark, marginTop: 4 }}>
            <span style={{ color: C.blue, fontWeight: 600 }}>Note: </span>
            {req.requestNote}
          </div>
        )}
      </td>

      <td style={cellStyle}>
        {hasCert ? (
          <button
            onClick={() => openCertificate(req)}
            style={openFileBtn}
            title="Open the certificate in a new browser tab"
          >
            <OpenInNewIcon /> Open file
          </button>
        ) : (
          <span style={{ color: C.textLight }}>-</span>
        )}
      </td>

      <td style={cellStyle}>
        <span style={{ color: C.textDark }}>
          {officerName ?? <span style={{ color: C.textLight }}>-</span>}
        </span>
      </td>

      <td style={cellStyle}>
        <DecisionPill status={req.status} />
      </td>
    </tr>
  );
}

function ProposedChangesInline({ changes, current }) {
  const entries = Object.entries(changes ?? {});
  if (entries.length === 0) return null;
  return (
    <div
      style={{
        marginTop: 6,
        padding: "6px 10px",
        background: C.amberLight + "55",
        borderRadius: 4,
        fontSize: 12,
        color: C.textDark,
        lineHeight: 1.5,
      }}
    >
      {entries.map(([k, v]) => (
        <div key={k}>
          <span style={{ color: C.textLight }}>{humanizeKey(k)}: </span>
          <span style={{ textDecoration: "line-through", color: C.textLight }}>
            {formatFieldValue(current?.[k]) || "(empty)"}
          </span>
          <span style={{ color: C.textLight }}>{" -> "}</span>
          <span style={{ fontWeight: 600 }}>
            {formatFieldValue(v) || "(empty)"}
          </span>
        </div>
      ))}
    </div>
  );
}

function DecisionPill({ status }) {
  if (status === "VERIFIED") {
    return (
      <span
        aria-label="Verified"
        title="Verified"
        style={decisionIcon(C.green)}
      >
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="white"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </span>
    );
  }
  if (status === "REJECTED") {
    return (
      <span aria-label="Rejected" title="Rejected" style={decisionIcon(C.red)}>
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="white"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </span>
    );
  }
  // PENDING - neutral pill (decide it from the inbox)
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "4px 12px",
        fontSize: 12,
        fontWeight: 600,
        color: C.textMed,
        background: C.divider,
        borderRadius: 14,
      }}
    >
      Pending
    </span>
  );
}

function decisionIcon(bg) {
  return {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 24,
    height: 24,
    borderRadius: "50%",
    background: bg,
  };
}

function getOfficerName(decidedBy) {
  if (!decidedBy) return null;
  return (
    decidedBy.institutionOfficer?.fullName ??
    decidedBy.organisationOfficer?.fullName ??
    null
  );
}

function metadataPairs(metadata) {
  if (!metadata || typeof metadata !== "object") return [];
  return Object.entries(metadata).filter(
    ([, v]) => v !== null && v !== "" && v !== undefined,
  );
}

function humanizeKey(key) {
  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (s) => s.toUpperCase())
    .replace(/Id$/, "ID");
}

function formatFieldValue(v) {
  if (v === null || v === undefined) return "";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "object") {
    if (v instanceof Date) return v.toLocaleDateString();
    return JSON.stringify(v);
  }
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v)) {
    const d = new Date(v);
    if (!isNaN(d.getTime()))
      return d.toLocaleDateString("en-IN", { month: "short", year: "numeric" });
  }
  return String(v);
}

function labelType(type) {
  return (
    { EDUCATION: "Education", WORK: "Work experience", OTHER: "Other" }[type] ??
    type
  );
}

function formatDateRange(start, end, isCurrent) {
  const fmt = (d) =>
    d
      ? new Date(d).toLocaleDateString("en-IN", {
          month: "short",
          year: "numeric",
        })
      : "";
  const s = fmt(start);
  const e = isCurrent ? "Present" : fmt(end);
  if (s && e) return `${s} - ${e}`;
  return s || e;
}

function OpenInNewIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}

const cellStyle = { padding: "13px 16px", verticalAlign: "top" };

const openFileBtn = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  padding: "6px 10px",
  fontSize: 12,
  fontWeight: 600,
  color: C.blue,
  background: "transparent",
  border: `1px solid ${C.blue}`,
  borderRadius: 4,
  cursor: "pointer",
  fontFamily: font,
  whiteSpace: "nowrap",
};

function Tab({ label, count, active, onClick }) {
  return (
    <button
      onClick={onClick}
      type="button"
      style={{
        padding: "10px 14px",
        fontSize: 13,
        fontWeight: 600,
        color: active ? C.blue : C.textMed,
        background: "transparent",
        border: 0,
        borderBottom: active ? `2px solid ${C.blue}` : "2px solid transparent",
        marginBottom: -1,
        cursor: "pointer",
        fontFamily: font,
        display: "flex",
        alignItems: "center",
        gap: 6,
        transition: "color 0.12s ease, border-color 0.12s ease",
      }}
    >
      {label}
      <span
        style={{
          padding: "1px 8px",
          fontSize: 10,
          fontWeight: 700,
          color: active ? C.blueDark : C.textLight,
          background: active ? C.blueLight : C.divider,
          borderRadius: 10,
          letterSpacing: 0.4,
        }}
      >
        {count}
      </span>
    </button>
  );
}

function StatCard({ label, value, highlight }) {
  return (
    <div
      style={{
        padding: 16,
        background: highlight ? C.blueLight : C.bg,
        border: `1px solid ${highlight ? C.blue + "33" : C.divider}`,
        borderRadius: 8,
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: C.textMed,
          textTransform: "uppercase",
          letterSpacing: 0.4,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 32,
          fontWeight: 700,
          color: highlight ? C.blueDark : C.textDark,
          marginTop: 6,
          fontVariantNumeric: "tabular-nums",
          lineHeight: 1,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

const cardStyle = {
  background: C.bg,
  border: `1px solid ${C.divider}`,
  borderRadius: 8,
  padding: 24,
};

// ---- HR applicants section ----
// The applicants to this organisation's own jobs, as the server lists them
// (it scopes the list to the organisation). Nothing is kept in the browser:
// a list that could not be read, or a status that was not saved, is
// reported rather than shown as if it were current.
function ApplicantsSection({ orgId }) {
  const [applicantTab, setApplicantTab] = useState("ALL"); // "ALL" | "SHORTLISTED" | "REJECTED"
  const [selectedCandidateModal, setSelectedCandidateModal] = useState(null);
  const [applicants, setApplicants] = useState([]);
  const [applicantsLoaded, setApplicantsLoaded] = useState(false);
  const [applicantsError, setApplicantsError] = useState(null);

  const syncApplicants = () => {
    fetchHRApplicantsApi()
      .then((data) => {
        setApplicants(Array.isArray(data) ? data : []);
        setApplicantsLoaded(true);
        setApplicantsError(null);
      })
      .catch((err) => {
        setApplicantsError(
          err?.message ??
            "Could not load the applicants. Refresh to try again.",
        );
      });
  };

  useEffect(() => {
    // Clean up un-scoped legacy test data from other sessions, and the copy
    // of the applicants this page used to keep in the browser.
    try {
      localStorage.removeItem("hr_job_applicants");
      localStorage.removeItem("published_opportunities");
      if (orgId) localStorage.removeItem(`hr_job_applicants_${orgId}`);
    } catch {
      // Ignore
    }
    syncApplicants();
    const handleStorageChange = () => syncApplicants();
    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("applicantStatusUpdated", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("applicantStatusUpdated", handleStorageChange);
    };
  }, [orgId]);

  const updateApplicantStatus = async (id, newStatus) => {
    setApplicantsError(null);
    try {
      await updateApplicantStatusApi(id, newStatus);
    } catch (err) {
      setApplicantsError(
        err?.message ?? "Could not change the applicant's status.",
      );
      return;
    }

    setApplicants((current) =>
      current.map((app) =>
        String(app.id) === String(id) ? { ...app, status: newStatus } : app,
      ),
    );
    window.dispatchEvent(new Event("storage"));
    window.dispatchEvent(
      new CustomEvent("applicantStatusUpdated", {
        detail: { id, status: newStatus },
      }),
    );
  };

  const filteredApplicants = applicants.filter((app) => {
    if (applicantTab === "SHORTLISTED") return app.status === "SHORTLISTED";
    if (applicantTab === "REJECTED") return app.status === "REJECTED";
    return true;
  });

  return (
    <section style={{ ...cardStyle, marginBottom: 16, padding: 28 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 20,
        }}
      >
        <div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
            <h2
              style={{
                fontSize: 20,
                fontWeight: 800,
                color: C.navy,
                margin: 0,
                letterSpacing: -0.3,
              }}
            >
              Candidate Applicants & Shared Profiles
              {applicantsLoaded && ` (${applicants.length})`}
            </h2>
          </div>
          <div style={{ fontSize: 13, color: C.textMed, marginTop: 4 }}>
            Profiles shared by candidates who applied to your jobs
          </div>

          {/* Three tabs: ALL, SHORTLISTED, REJECTED */}
          <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
            {[
              { key: "ALL", label: "All Applicants" },
              { key: "SHORTLISTED", label: "Shortlisted" },
              { key: "REJECTED", label: "Rejected" },
            ].map((tab) => {
              const count =
                tab.key === "ALL"
                  ? applicants.length
                  : applicants.filter((a) => a.status === tab.key).length;
              const active = applicantTab === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => setApplicantTab(tab.key)}
                  style={{
                    padding: "6px 14px",
                    borderRadius: 6,
                    border: 0,
                    background: active ? C.primary : "#f1f5f9",
                    color: active ? "#fff" : C.textDark,
                    fontWeight: 700,
                    fontSize: 12,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    boxShadow: active ? "0 2px 4px rgba(0,0,0,0.1)" : "none",
                  }}
                >
                  <span>{tab.label}</span>
                  <span
                    style={{
                      background: active ? "rgba(255,255,255,0.25)" : C.divider,
                      color: active ? "#fff" : C.textDark,
                      padding: "2px 6px",
                      borderRadius: 10,
                      fontSize: 10,
                      fontWeight: 800,
                    }}
                  >
                    {applicantsLoaded ? count : "-"}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              background: C.greenLight,
              color: C.green,
              padding: "4px 12px",
              borderRadius: 12,
            }}
          >
            Real-time Submissions
          </span>
        </div>
      </div>

      {applicantsError && (
        <div
          role="alert"
          className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-xs sm:text-sm font-semibold"
        >
          {applicantsError}
        </div>
      )}

      {!applicantsLoaded && !applicantsError && (
        <div className="p-6 text-center text-sm text-slate-500">
          Loading applicants...
        </div>
      )}

      {applicantsLoaded && filteredApplicants.length === 0 ? (
        <div
          style={{
            padding: 24,
            textAlign: "center",
            color: C.textMed,
            fontSize: 13,
            background: "#f8fafc",
            borderRadius: 8,
            border: `1px solid ${C.divider}`,
          }}
        >
          No applicants found in <strong>{applicantTab}</strong> filter.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {filteredApplicants.map((app) => (
            <div
              key={app.id}
              style={{
                border: `1px solid ${C.divider}`,
                padding: 20,
                borderRadius: 10,
                background: "#f8fafc",
              }}
            >
              {/* Header Row: Candidate Info (Left) | Horizontal Action Buttons (Top Right) */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 16,
                  flexWrap: "wrap",
                  marginBottom: 6,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    flexWrap: "wrap",
                  }}
                >
                  <h3
                    style={{
                      fontSize: 18,
                      fontWeight: 800,
                      color: C.navy,
                      margin: 0,
                    }}
                  >
                    {app.candidateName}
                  </h3>
                  {app.status === "SHORTLISTED" && (
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 800,
                        background: "#dcfce7",
                        color: "#166534",
                        padding: "2px 8px",
                        borderRadius: 10,
                      }}
                    >
                      Shortlisted
                    </span>
                  )}
                  {app.status === "REJECTED" && (
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 800,
                        background: "#fee2e2",
                        color: "#991b1b",
                        padding: "2px 8px",
                        borderRadius: 10,
                      }}
                    >
                      Rejected
                    </span>
                  )}
                </div>

                {/* Horizontal Action Buttons in Top Right */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 8,
                    flexWrap: "wrap",
                  }}
                >
                  <button
                    onClick={() => setSelectedCandidateModal(app)}
                    style={{
                      padding: "8px 14px",
                      borderRadius: 6,
                      border: `1px solid ${C.primary}`,
                      background: C.primaryLight,
                      color: C.primary,
                      fontWeight: 700,
                      fontSize: 12,
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 6,
                      whiteSpace: "nowrap",
                    }}
                  >
                    View Candidate
                  </button>

                  {app.status === "SHORTLISTED" ? (
                    <button
                      disabled
                      style={{
                        padding: "8px 14px",
                        borderRadius: 6,
                        border: 0,
                        background: "#15803d",
                        color: "#fff",
                        fontWeight: 700,
                        fontSize: 12,
                        cursor: "not-allowed",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 4,
                        whiteSpace: "nowrap",
                      }}
                    >
                      <InlineIcon name="check" size={12} /> Shortlisted
                    </button>
                  ) : (
                    <button
                      onClick={() =>
                        updateApplicantStatus(app.id, "SHORTLISTED")
                      }
                      style={{
                        padding: "8px 14px",
                        borderRadius: 6,
                        border: 0,
                        background: C.green,
                        color: "#fff",
                        fontWeight: 700,
                        fontSize: 12,
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Shortlist Candidate
                    </button>
                  )}

                  {app.status === "REJECTED" ? (
                    <button
                      disabled
                      style={{
                        padding: "8px 14px",
                        borderRadius: 6,
                        border: 0,
                        background: "#991b1b",
                        color: "#fff",
                        fontWeight: 700,
                        fontSize: 12,
                        cursor: "not-allowed",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <InlineIcon name="close" size={12} /> Rejected
                    </button>
                  ) : (
                    <button
                      onClick={() => updateApplicantStatus(app.id, "REJECTED")}
                      style={{
                        padding: "8px 14px",
                        borderRadius: 6,
                        border: `1px solid ${C.red}`,
                        background: "#fff",
                        color: C.red,
                        fontWeight: 700,
                        fontSize: 12,
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Reject Application
                    </button>
                  )}
                </div>
              </div>

              <div
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: C.primary,
                  marginTop: 2,
                }}
              >
                Applied for: {app.jobTitle}
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: 10,
                  fontSize: 12,
                  background: "#fff",
                  padding: 14,
                  borderRadius: 8,
                  border: `1px solid ${C.divider}`,
                  marginTop: 12,
                }}
              >
                <div>
                  <span style={{ color: C.textMed }}>Email:</span>{" "}
                  <strong>{app.email}</strong>
                </div>
                <div>
                  <span style={{ color: C.textMed }}>Contact:</span>{" "}
                  <strong>{app.contactNumber}</strong>
                </div>
                <div>
                  <span style={{ color: C.textMed }}>Location:</span>{" "}
                  <strong>{app.location || "-"}</strong>
                </div>
                <div>
                  <span style={{ color: C.textMed }}>Education:</span>{" "}
                  <strong>{app.education}</strong>
                </div>
                <div>
                  <span style={{ color: C.textMed }}>Institution:</span>{" "}
                  <strong>{app.institution}</strong>
                </div>
                <div>
                  <span style={{ color: C.textMed }}>Experience:</span>{" "}
                  <strong>{app.experience}</strong>
                </div>
                <div>
                  <span style={{ color: C.textMed }}>Notice Period:</span>{" "}
                  <strong>{app.noticePeriod}</strong>
                </div>
                <div style={{ gridColumn: "span 2" }}>
                  <span style={{ color: C.textMed }}>Skillset:</span>{" "}
                  <strong>{app.skillset}</strong>
                </div>
              </div>

              <div style={{ fontSize: 11, color: C.textMed, marginTop: 10 }}>
                Applied on {app.appliedAt || formatDate(app.createdAt)}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Candidate Details Modal */}
      {selectedCandidateModal && (
        <CandidateModal
          candidate={selectedCandidateModal}
          onClose={() => setSelectedCandidateModal(null)}
          onUpdateStatus={updateApplicantStatus}
        />
      )}
    </section>
  );
}

// ---- candidate document download ----
// Saves a file the applicant attached, exactly as it was sent. With no file
// there is nothing to save: no stand-in document is made up.
function downloadDocument(fileName, candidateName, docType, dataUrl) {
  if (!isAttachedFile(dataUrl)) return;
  const safeName = (candidateName || "Candidate").replace(/\s+/g, "_");
  const link = document.createElement("a");
  link.href = dataUrl;
  link.download = fileName || `${safeName}_${docType}`;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

// ---- candidate modal ----
function CandidateModal({ candidate, onClose, onUpdateStatus }) {
  if (!candidate) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.6)",
        backdropFilter: "blur(3px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: 20,
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 12,
          width: "100%",
          maxWidth: 680,
          maxHeight: "88vh",
          overflowY: "auto",
          padding: 24,
          boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            borderBottom: `1px solid ${C.divider}`,
            paddingBottom: 14,
            marginBottom: 16,
          }}
        >
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <h2
                style={{
                  fontSize: 20,
                  fontWeight: 800,
                  color: C.navy,
                  margin: 0,
                }}
              >
                {candidate.candidateName || "Candidate Profile"}
              </h2>
              {candidate.verified === true && (
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 800,
                    background: C.blueLight,
                    color: C.blue,
                    padding: "2px 8px",
                    borderRadius: 10,
                  }}
                >
                  Verified Profile
                </span>
              )}
            </div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: C.blue,
                marginTop: 4,
              }}
            >
              Applied for: {candidate.jobTitle}
            </div>
            <div style={{ fontSize: 12, color: C.textMed, marginTop: 2 }}>
              Submitted on{" "}
              {candidate.appliedAt || formatDate(candidate.createdAt)}
            </div>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            style={{
              background: "transparent",
              border: 0,
              fontSize: 22,
              cursor: "pointer",
              color: C.textMed,
            }}
          >
            <InlineIcon name="close" size={20} />
          </button>
        </div>

        {/* Details Grid */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 14,
            fontSize: 13,
          }}
        >
          <div
            style={{
              background: "#f8fafc",
              border: `1px solid ${C.divider}`,
              borderRadius: 8,
              padding: 14,
            }}
          >
            <h4
              style={{
                fontSize: 13,
                fontWeight: 800,
                color: C.blue,
                margin: "0 0 8px",
                borderBottom: `1px solid ${C.divider}`,
                paddingBottom: 4,
              }}
            >
              Contact & Personal Details
            </h4>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 8,
              }}
            >
              <div>
                <strong>Full Name:</strong> {candidate.candidateName || "-"}
              </div>
              <div>
                <strong>Email Address:</strong> {candidate.email || "-"}
              </div>
              <div>
                <strong>Contact Number:</strong>{" "}
                {candidate.contactNumber || "-"}
              </div>
              <div>
                <strong>Current Location:</strong> {candidate.location || "-"}
              </div>
            </div>
          </div>

          <div
            style={{
              background: "#f8fafc",
              border: `1px solid ${C.divider}`,
              borderRadius: 8,
              padding: 14,
            }}
          >
            <h4
              style={{
                fontSize: 13,
                fontWeight: 800,
                color: C.blue,
                margin: "0 0 8px",
                borderBottom: `1px solid ${C.divider}`,
                paddingBottom: 4,
              }}
            >
              Academic Background
            </h4>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 8,
              }}
            >
              <div>
                <strong>Qualification:</strong> {candidate.education || "-"}
              </div>
              <div>
                <strong>Institution:</strong> {candidate.institution || "-"}
              </div>
            </div>
          </div>

          <div
            style={{
              background: "#f8fafc",
              border: `1px solid ${C.divider}`,
              borderRadius: 8,
              padding: 14,
            }}
          >
            <h4
              style={{
                fontSize: 13,
                fontWeight: 800,
                color: C.blue,
                margin: "0 0 8px",
                borderBottom: `1px solid ${C.divider}`,
                paddingBottom: 4,
              }}
            >
              Skills & Experience
            </h4>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 8,
              }}
            >
              <div>
                <strong>Experience:</strong> {candidate.experience || "-"}
              </div>
              <div>
                <strong>Notice Period:</strong> {candidate.noticePeriod || "-"}
              </div>
              <div style={{ gridColumn: "span 2" }}>
                <strong>Skillset:</strong> {candidate.skillset || "-"}
              </div>
            </div>
          </div>

          <div
            style={{
              background: "#f8fafc",
              border: `1px solid ${C.divider}`,
              borderRadius: 8,
              padding: 14,
            }}
          >
            <h4
              style={{
                fontSize: 13,
                fontWeight: 800,
                color: C.blue,
                margin: "0 0 10px",
                borderBottom: `1px solid ${C.divider}`,
                paddingBottom: 6,
              }}
            >
              Uploaded Resume & CV
            </h4>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 12,
              }}
            >
              {/* Resume Box & Download Button */}
              <div
                style={{
                  background: "#fff",
                  padding: 12,
                  borderRadius: 6,
                  border: `1px solid ${C.divider}`,
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                }}
              >
                <div
                  style={{ fontSize: 12, color: C.textMed, fontWeight: 700 }}
                >
                  Resume Document:
                </div>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: C.navy,
                    wordBreak: "break-all",
                  }}
                >
                  {attachmentLabel(
                    candidate.resumeFile,
                    candidate.resumeDataUrl,
                  )}
                </div>
                {isAttachedFile(candidate.resumeDataUrl) && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      downloadDocument(
                        candidate.resumeFile,
                        candidate.candidateName,
                        "Resume",
                        candidate.resumeDataUrl,
                      );
                    }}
                    style={{
                      marginTop: 4,
                      padding: "7px 14px",
                      borderRadius: 6,
                      border: 0,
                      background: C.green,
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: 12,
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      width: "fit-content",
                    }}
                  >
                    Download Resume
                  </button>
                )}
              </div>

              {/* CV Box & Download Button */}
              <div
                style={{
                  background: "#fff",
                  padding: 12,
                  borderRadius: 6,
                  border: `1px solid ${C.divider}`,
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                }}
              >
                <div
                  style={{ fontSize: 12, color: C.textMed, fontWeight: 700 }}
                >
                  Curriculum Vitae (CV):
                </div>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: C.navy,
                    wordBreak: "break-all",
                  }}
                >
                  {attachmentLabel(candidate.cvFile, candidate.cvDataUrl)}
                </div>
                {isAttachedFile(candidate.cvDataUrl) && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      downloadDocument(
                        candidate.cvFile,
                        candidate.candidateName,
                        "CV",
                        candidate.cvDataUrl,
                      );
                    }}
                    style={{
                      marginTop: 4,
                      padding: "7px 14px",
                      borderRadius: 6,
                      border: `1px solid ${C.blue}`,
                      background: C.blueLight,
                      color: C.blue,
                      fontWeight: 700,
                      fontSize: 12,
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      width: "fit-content",
                    }}
                  >
                    Download CV
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Modal Action Buttons */}
        <div
          style={{
            marginTop: 20,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderTop: `1px solid ${C.divider}`,
            paddingTop: 14,
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 700 }}>
            Status:{" "}
            {candidate.status === "SHORTLISTED" ? (
              <span
                style={{
                  color: C.green,
                  background: C.greenLight,
                  padding: "3px 8px",
                  borderRadius: 10,
                }}
              >
                <InlineIcon name="check" size={12} /> Shortlisted
              </span>
            ) : candidate.status === "REJECTED" ? (
              <span
                style={{
                  color: C.red,
                  background: C.redLight,
                  padding: "3px 8px",
                  borderRadius: 10,
                }}
              >
                <InlineIcon name="close" size={12} /> Rejected
              </span>
            ) : (
              <span
                style={{
                  color: C.blue,
                  background: C.blueLight,
                  padding: "3px 8px",
                  borderRadius: 10,
                }}
              >
                Under Review
              </span>
            )}
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              onClick={() => {
                onUpdateStatus(candidate.id, "SHORTLISTED");
                onClose();
              }}
              style={{
                padding: "8px 16px",
                borderRadius: 6,
                border: 0,
                background:
                  candidate.status === "SHORTLISTED" ? "#166534" : C.green,
                color: "#fff",
                fontWeight: 700,
                cursor: "pointer",
                fontSize: 12,
              }}
            >
              {candidate.status === "SHORTLISTED" ? (
                <>
                  <InlineIcon name="check" size={12} /> Shortlisted
                </>
              ) : (
                "Shortlist Candidate"
              )}
            </button>

            <button
              type="button"
              onClick={() => {
                onUpdateStatus(candidate.id, "REJECTED");
                onClose();
              }}
              style={{
                padding: "8px 16px",
                borderRadius: 6,
                border: `1px solid ${C.red}`,
                background:
                  candidate.status === "REJECTED" ? C.redLight : "#fff",
                color: C.red,
                fontWeight: 700,
                cursor: "pointer",
                fontSize: 12,
              }}
            >
              {candidate.status === "REJECTED" ? (
                <>
                  <InlineIcon name="close" size={12} /> Rejected
                </>
              ) : (
                "Reject"
              )}
            </button>

            <button
              type="button"
              onClick={onClose}
              style={{
                padding: "8px 16px",
                borderRadius: 6,
                border: `1px solid ${C.divider}`,
                background: "#fff",
                color: C.textDark,
                fontWeight: 700,
                cursor: "pointer",
                fontSize: 12,
              }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
